import {
  FRAME_HEIGHT,
  FRAME_WIDTH,
  TIKTOK_GUARD,
  safeArea,
  watermarkBox,
} from '@efira/game-sdk/frame';

import type { ElementPlacement, HudElementSpec, OverlayLayout } from '../api/queries';

/**
 * Геометрия редактора раскладки - без React и без DOM.
 *
 * Отдельным модулем, потому что снап, направляющие и предупреждения об
 * охранных зонах - это арифметика, и проверять её надо тестом, а не глазами на
 * перетаскивании. Пять дефектов раскладки первой игры были именно арифметикой.
 */

/* `watermarkBox` переэкспортируется, а не дублируется: координаты знака
   берут и оверлей, и редактор раскладки, и разъехаться они не должны. */
export { FRAME_HEIGHT, FRAME_WIDTH, TIKTOK_GUARD, safeArea, watermarkBox };

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Куда элемент встанет при этой раскладке - в логических пикселях кадра. */
export function boxOf(element: HudElementSpec, placement: ElementPlacement | undefined): Box {
  const scale = placement?.scale ?? 1;
  const stretch = placement?.stretch ?? 1;

  const width = element.stretch === 'x' ? element.width * stretch * scale : element.width * scale;
  const height =
    element.stretch === 'y' ? element.height * stretch * scale : element.height * scale;

  return {
    x: element.x + (placement?.x ?? 0),
    y: element.y + (placement?.y ?? 0),
    width,
    height,
  };
}

/** Три охранные зоны в логических пикселях - для отрисовки поверх кадра. */
export function guardZones(): { id: string; title: string; box: Box }[] {
  const safe = safeArea();

  return [
    {
      id: 'top',
      title: 'Здесь шапка эфира',
      box: { x: 0, y: 0, width: FRAME_WIDTH, height: safe.top },
    },
    {
      id: 'bottom',
      title: 'Здесь лента комментариев',
      box: { x: 0, y: safe.bottom, width: FRAME_WIDTH, height: FRAME_HEIGHT - safe.bottom },
    },
    {
      id: 'right',
      title: 'Здесь кнопки',
      box: {
        x: safe.right,
        y: safe.top,
        width: FRAME_WIDTH - safe.right,
        height: safe.bottom - safe.top,
      },
    },
  ];
}

/** Порог притяжения в логических пикселях. */
export const SNAP = 12;

export interface SnapResult {
  x: number;
  y: number;
  /** Направляющие, которые надо показать: вертикальные и горизонтальные. */
  guides: { vertical: number[]; horizontal: number[] };
}

/**
 * Притяжение к краям безопасной области, центру кадра и осям соседей.
 *
 * ЧЕГО У КОНКУРЕНТА НЕТ ВОВСЕ: ни сетки, ни привязки к краям и центру, ни
 * направляющих. Позиции хранятся дробными (`x: -474.2725`), и аккуратную
 * раскладку можно собрать только на глаз (`OVERLAY_LAYOUT.md`, 1.4 и 5.3).
 *
 * Притяжение отключается модификатором - это требование, а не удобство:
 * иногда элемент нужно поставить именно туда, куда снап не пускает.
 */
export function snap(box: Box, others: readonly Box[], enabled: boolean): SnapResult {
  if (!enabled) {
    return { x: box.x, y: box.y, guides: { vertical: [], horizontal: [] } };
  }

  const safe = safeArea();

  const verticalTargets = [
    safe.left,
    safe.right,
    FRAME_WIDTH / 2,
    ...others.flatMap((other) => [other.x, other.x + other.width / 2, other.x + other.width]),
  ];

  const horizontalTargets = [
    safe.top,
    safe.bottom,
    FRAME_HEIGHT / 2,
    ...others.flatMap((other) => [other.y, other.y + other.height / 2, other.y + other.height]),
  ];

  // Притягиваются три точки элемента: левый край, центр и правый край. Иначе
  // выровнять по центру можно было бы только случайно.
  const anchorsX = [box.x, box.x + box.width / 2, box.x + box.width];
  const anchorsY = [box.y, box.y + box.height / 2, box.y + box.height];

  const guides: SnapResult['guides'] = { vertical: [], horizontal: [] };
  let x = box.x;
  let y = box.y;
  let bestX = SNAP;
  let bestY = SNAP;

  for (const [index, anchor] of anchorsX.entries()) {
    for (const target of verticalTargets) {
      const distance = Math.abs(anchor - target);

      if (distance < bestX) {
        bestX = distance;
        x = box.x + (target - anchor);
        guides.vertical = [target];
      }
    }
    void index;
  }

  for (const [index, anchor] of anchorsY.entries()) {
    for (const target of horizontalTargets) {
      const distance = Math.abs(anchor - target);

      if (distance < bestY) {
        bestY = distance;
        y = box.y + (target - anchor);
        guides.horizontal = [target];
      }
    }
    void index;
  }

  return { x, y, guides };
}

/**
 * Смещение, ограниченное кадром.
 *
 * Элемент можно увести в охранную зону - это предупреждение, а не запрет, - но
 * не за пределы кадра: оттуда его не вернуть мышью, а стример увидит пустой
 * overlay без объяснения. У конкурента ограничение ровно такое же, и это
 * единственное, что в его редакторе сделано правильно.
 */
export function clampToFrame(
  element: HudElementSpec,
  placement: ElementPlacement,
): ElementPlacement {
  const box = boxOf(element, placement);
  const minX = -element.x;
  const maxX = FRAME_WIDTH - element.x - Math.min(box.width, FRAME_WIDTH / 4);
  const minY = -element.y;
  const maxY = FRAME_HEIGHT - element.y - Math.min(box.height, FRAME_HEIGHT / 8);

  return {
    ...placement,
    x: Math.max(minX, Math.min(maxX, placement.x ?? 0)),
    y: Math.max(minY, Math.min(maxY, placement.y ?? 0)),
  };
}

/** Записи только для тронутых элементов: пустая запись удаляется. */
export function prune(layout: OverlayLayout): OverlayLayout {
  const result: OverlayLayout = {};

  for (const [id, placement] of Object.entries(layout)) {
    const meaningful =
      (placement.x !== undefined && Math.abs(placement.x) > 0.5) ||
      (placement.y !== undefined && Math.abs(placement.y) > 0.5) ||
      (placement.scale !== undefined && Math.abs(placement.scale - 1) > 0.01) ||
      (placement.stretch !== undefined && Math.abs(placement.stretch - 1) > 0.01) ||
      /* Слой, отличный от заводского, - такая же правка, как смещение.
         Без этой строки «поднять над соседями» молча не сохранялось: запись
         выбрасывалась как «ничего не изменилось». */
      (placement.z !== undefined && placement.z !== 1) ||
      placement.hidden === true;

    if (meaningful) {
      result[id] = placement;
    }
  }

  return result;
}
